<template>
  <!-- Decorative only. The state is always carried by the heading and body copy
       beside it, so the art is hidden from assistive tech rather than labelled. -->
  <div
    aria-hidden="true"
    class="relative isolate flex items-center justify-center pointer-events-none select-none rtl:-scale-x-100"
    :class="sizeClass"
  >
    <!-- The one place mint is allowed to be a fill: as light. It sits BEHIND the
         line art at a low alpha so the art itself stays a single flat colour and
         the accent reads as the product being alive, not as decoration. -->
    <span
      class="absolute inset-x-6 top-1/4 -z-10 h-1/2 rounded-full blur-2xl bg-mint/[0.06] dark:bg-mint/[0.12]"
    />

    <svg
      viewBox="0 0 176 116"
      fill="none"
      focusable="false"
      class="w-full h-auto text-slate-500 dark:text-slate-300"
      xmlns="http://www.w3.org/2000/svg"
    >
      <!-- CONVERSATION — a receding stack of conversation rows with one live.
           The product's own shape, not a picture of a chat: the two rows behind
           fade back, the front one is fully drawn, and a single accent dot says
           one of them is waiting for you. -->
      <g v-if="variant === 'conversation'" stroke-linecap="round">
        <rect
          x="34"
          y="7"
          width="108"
          height="22"
          rx="7"
          stroke="currentColor"
          stroke-width="1.5"
          opacity=".2"
        />
        <rect
          x="21"
          y="35"
          width="134"
          height="26"
          rx="8"
          stroke="currentColor"
          stroke-width="1.5"
          opacity=".4"
        />
        <rect
          x="8"
          y="67"
          width="160"
          height="42"
          rx="11"
          fill="currentColor"
          fill-opacity=".05"
          stroke="currentColor"
          stroke-width="1.5"
        />
        <circle
          cx="32"
          cy="88"
          r="9"
          stroke="currentColor"
          stroke-width="1.5"
          opacity=".55"
        />
        <rect
          x="50"
          y="80"
          width="74"
          height="6"
          rx="3"
          fill="currentColor"
          fill-opacity=".38"
        />
        <rect
          x="50"
          y="92"
          width="52"
          height="6"
          rx="3"
          fill="currentColor"
          fill-opacity=".2"
        />
        <circle cx="150" cy="88" r="4.5" class="fill-accent-mint" />
      </g>

      <!-- INBOX — channels feeding one tray. Three pills drop into the tray on
           dashed rails; the live one is the accent. -->
      <g v-else-if="variant === 'inbox'" stroke-linecap="round">
        <rect
          x="40"
          y="8"
          width="22"
          height="9"
          rx="4.5"
          fill="currentColor"
          fill-opacity=".22"
        />
        <rect
          x="77"
          y="8"
          width="22"
          height="9"
          rx="4.5"
          class="fill-accent-mint"
        />
        <rect
          x="114"
          y="8"
          width="22"
          height="9"
          rx="4.5"
          fill="currentColor"
          fill-opacity=".22"
        />
        <path
          d="M51 24v12M125 24v12"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-dasharray="2 5"
          opacity=".45"
        />
        <path
          d="M88 24v12"
          class="stroke-accent-mint"
          stroke-width="1.5"
          stroke-dasharray="2 5"
        />
        <rect
          x="20"
          y="44"
          width="136"
          height="62"
          rx="12"
          fill="currentColor"
          fill-opacity=".04"
          stroke="currentColor"
          stroke-width="1.5"
        />
        <path
          d="M20 72h32l8 12h56l8-12h32"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linejoin="round"
        />
      </g>

      <!-- SEARCH — rows dissolving behind a lens. Nothing matched, so the rows
           fade out as they recede and the lens is the only thing fully drawn. -->
      <g v-else-if="variant === 'search'" stroke-linecap="round">
        <rect
          x="14"
          y="26"
          width="94"
          height="7"
          rx="3.5"
          fill="currentColor"
          fill-opacity=".16"
        />
        <rect
          x="14"
          y="46"
          width="70"
          height="7"
          rx="3.5"
          fill="currentColor"
          fill-opacity=".11"
        />
        <rect
          x="14"
          y="66"
          width="84"
          height="7"
          rx="3.5"
          fill="currentColor"
          fill-opacity=".07"
        />
        <circle
          cx="118"
          cy="52"
          r="28"
          fill="currentColor"
          fill-opacity=".04"
          stroke="currentColor"
          stroke-width="1.5"
        />
        <path d="M138 72l16 16" class="stroke-accent-mint" stroke-width="3" />
      </g>

      <!-- MISSING — the outline of a record that is not there. Dashed frame, a
           broken row inside, and one solid mark where the anchor should be. -->
      <g v-else stroke-linecap="round">
        <rect
          x="18"
          y="26"
          width="140"
          height="66"
          rx="12"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-dasharray="7 8"
          opacity=".5"
        />
        <rect
          x="42"
          y="46"
          width="42"
          height="7"
          rx="3.5"
          fill="currentColor"
          fill-opacity=".22"
        />
        <rect
          x="98"
          y="46"
          width="38"
          height="7"
          rx="3.5"
          fill="currentColor"
          fill-opacity=".22"
        />
        <rect
          x="42"
          y="66"
          width="62"
          height="7"
          rx="3.5"
          fill="currentColor"
          fill-opacity=".14"
        />
        <circle cx="88" cy="26" r="5.5" class="fill-accent-sky" />
      </g>
    </svg>
  </div>
</template>

<script>
const VARIANTS = ['conversation', 'inbox', 'search', 'missing'];

export default {
  props: {
    // Which arrangement of the product's own shapes to draw.
    variant: {
      type: String,
      default: 'conversation',
      validator: value => VARIANTS.includes(value),
    },
    // `md` is the full-panel empty state; `sm` is for a state that sits inside a
    // list or a section rather than owning the screen.
    size: {
      type: String,
      default: 'md',
      validator: value => ['sm', 'md'].includes(value),
    },
  },
  computed: {
    sizeClass() {
      return this.size === 'sm' ? 'w-28' : 'w-44';
    },
  },
};
</script>
